import type { Option } from "../../core/option.ts";
import type { DateUnit, NaiveDate, TimezoneRegion } from "@tento-chrono";

import {
  makeEntryFormulaAdapter,
  makeRowFormulaAdapter,
  makeSheetFormulaAdapter,
} from "./data-series-formula.ts";
import { isRendererWireType, rendererSlotKindOf } from "../../render/renderer-kind.ts";
import { Json, type JsonObject } from "../../core/json.ts";
import type { BarWidthY, Graph, NumRangeLike, OhlcY, PointShape, PointY, RecordY } from "../../stores/graph-types.ts";
import type { CellValue } from "../../core/dtype.ts";

/**
 * A data series' key together with the point shape the caller reads from
 * it: `DataIndex.peekPointsInRange(SeriesKey.scalar("default"), …)` returns
 * `[x, number]` points. A series' adapter decides what each point's `Y` is,
 * so the query keeps the points whose `Y` has the key's shape and skips the
 * rest (a record point is never handed to a scalar reader). `SeriesKey.any`
 * reads every point as a `PointY`.
 */
export class SeriesKey<Y extends PointY> {
  private constructor(
    /** The series' `key` (a `data-series` row's `seriesKey`; `"default"` for most presets). */
    readonly id: string,
    /** Whether a point's `Y` has this key's shape. */
    readonly probe: (y: PointY) => y is Y,
  ) {}

  /** Points whose `Y` is a finite number. */
  static scalar(id: string): SeriesKey<number> {
    return new SeriesKey(id, (y): y is number => typeof y === "number");
  }

  /** Points whose `Y` is an `OhlcY` (finite `open`/`high`/`low`/`close`). */
  static ohlc(id: string): SeriesKey<OhlcY> {
    return new SeriesKey(id, isOhlcY);
  }

  /** Points whose `Y` is a `[value, width]` pair. */
  static barWidth(id: string): SeriesKey<BarWidthY> {
    return new SeriesKey(id, (y): y is BarWidthY => Array.isArray(y));
  }

  /** Points whose `Y` is a record (any object that is not an array). */
  static record(id: string): SeriesKey<RecordY> {
    return new SeriesKey(
      id,
      (y): y is RecordY => typeof y === "object" && !Array.isArray(y),
    );
  }

  /** Every point, its `Y` read as a `PointY`. */
  static any(id: string): SeriesKey<PointY> {
    return new SeriesKey(id, (y): y is PointY => y !== undefined);
  }
}

function isOhlcY(y: PointY): y is OhlcY {
  if (typeof y !== "object" || Array.isArray(y)) return false;
  const record: RecordY = y;
  return ["open", "high", "low", "close"].every((field) => {
    const value = record[field];
    return typeof value === "number" && Number.isFinite(value);
  });
}

/**
 * Minimal interface satisfied by both SubtreeSpec and SheetTreeSpecNode.
 * Used by propsFromNodes() to walk any tree shape generically.
 */
export interface GraphSpecNode {
  readonly type: string;
  readonly label?: string;
  readonly formula?: string;
  readonly attributes?: JsonObject;
  /** The node's closures (see `Graph.Hooks`); absent for rows-driven charts. */
  readonly graph?: Graph.Hooks;
  readonly children?: readonly GraphSpecNode[];
}

/**
 * Derive Graph.Props from any tree of GraphSpecNode[].
 *
 * Walks data-canvas → extract yAxis, xPadding, toXRange, xDisplay, xTicks, xNow
 * Walks data-canvas children → collect RendererSpec[] (background z)
 * Walks data-series → collect SeriesSpec[]
 * Walks data-series children → collect RendererSpec[] (foreground z)
 */
export function propsFromNodes(specs: readonly GraphSpecNode[]): Graph.Props {
  let xPadding: Option<DateUnit.Parts.Opt> = null;
  let toXRange: Option<(r: NaiveDate.Range, tz: TimezoneRegion) => NumRangeLike> = null;
  let xDisplay: Option<(x: number, tz: TimezoneRegion) => string> = null;
  let xTicks: Option<(range: NumRangeLike) => number[]> = null;
  let xNow: Option<() => Option<number>> = null;
  let yAxis: Option<{ min: number; max: number; center: number }> = null;
  const series: Graph.SeriesSpec[] = [];
  const renderers: Graph.RendererSpec[] = [];

  for (const spec of specs) {
    if (spec.type === "data-canvas") {
      const a = spec.attributes ?? null;
      const hooks = spec.graph ?? null;
      if (hooks?.toXRange) toXRange = hooks.toXRange;
      if (hooks?.xDisplay) xDisplay = hooks.xDisplay;
      if (hooks?.xTicks) xTicks = hooks.xTicks;
      if (hooks?.xNow) xNow = hooks.xNow;
      const padding = decodeDateParts(Json.child(a, "xPadding"));
      if (padding != null) xPadding = padding;
      const axis = decodeAxisRange(Json.child(a, "yAxis"));
      if (axis != null) yAxis = axis;

      // Canvas children are renderers (background z), or a nested data-series
      // (its own renderers are foreground z). Nesting the series under the
      // canvas is how a chart subsumes its data rows so they don't render as
      // separate rows (see the stocks preset in data-presets.ts).
      for (const child of spec.children ?? []) {
        if (isRendererWireType(child.type)) {
          const r = rendererSpecFromNode(child);
          if (r) renderers.push(r);
        } else if (child.type === "data-series") {
          collectSeries(child, series, renderers);
        }
      }
    } else if (spec.type === "data-series") {
      collectSeries(spec, series, renderers);
    } else if (isRendererWireType(spec.type)) {
      // Standalone renderer — z-position determined by sibling order
      const r = rendererSpecFromNode(spec);
      if (r) renderers.push(r);
    }
  }

  const props: Graph.Props = { yAxis, series, renderers };
  if (xPadding != null) props.xPadding = xPadding;
  if (toXRange != null) props.toXRange = toXRange;
  if (xDisplay != null) props.xDisplay = xDisplay;
  if (xTicks != null) props.xTicks = xTicks;
  if (xNow != null) props.xNow = xNow;
  return props;
}

/**
 * Extract a `data-series` spec into a SeriesSpec (+ its child data-renderer
 * specs). Shared by the top-level walk and the nested-under-canvas walk so a
 * series renders the same whether it is a sibling of the canvas or a subsumed
 * child of it.
 */
function collectSeries(
  spec: GraphSpecNode,
  series: Graph.SeriesSpec[],
  renderers: Graph.RendererSpec[],
): void {
  const a = spec.attributes ?? null;
  const seriesKey = Json.string(a, "seriesKey") ?? "default";
  const seriesType = Json.string(a, "seriesType") ?? "regression";
  const yRange = decodeAxisRange(Json.child(a, "yRange"));
  const convHalfWidth = Json.number(a, "convHalfWidth");
  const maxGap = Json.number(a, "maxGap");
  const minPoints = Json.number(a, "minPoints");
  const minCoverage = Json.number(a, "minCoverage");
  const adapter = spec.graph?.adapter ?? null;
  const groupAdapter = spec.graph?.groupAdapter ?? null;
  const dataSource = Json.string(a, "dataSource") ?? "builtin";
  const scope = Json.string(a, "scope") ?? "row";
  const xSource = Json.string(a, "xSource") === "date.mse" ? "date.mse" : "date.dse";
  const shapeAttr = Json.string(a, "shape");
  const shape: PointShape =
    shapeAttr === "ohlc" ? "ohlc"
    : shapeAttr === "bar-width" ? "bar-width"
    : shapeAttr === "record" ? "record"
    : "scalar";

  const s: Graph.SeriesSpec = {
    key: seriesKey,
    type: seriesType === "regression" ? "regression" : "default",
    label: spec.label ?? null,
  };
  if (spec.formula) s.formula = spec.formula;
  if (yRange) s.yRange = yRange;
  if (convHalfWidth != null) s.convHalfWidth = convHalfWidth;
  if (maxGap != null) s.maxGap = maxGap;
  if (minPoints != null) s.minPoints = minPoints;
  if (minCoverage != null) s.minCoverage = minCoverage;
  if (dataSource === "formula") {
    // Series-formula bridge (Track 2 / C1). The transform is a Lisp
    // expression — on `spec.formula` (preset convenience) or a child
    // `formula` row (wire/template model). `scope:"row"` (column cells,
    // X from the date), `scope:"entry"` (DTR cells, X from dtr.start), and
    // `scope:"sheet"` (one eval over all cells → `groupAdapter`, for
    // cross-cell aggregation via `group-by`) are all wired. The builtin
    // `_adapter`/`_groupAdapter` path is untouched (used when
    // dataSource !== formula).
    const formula = resolveSeriesFormula(spec);
    if (formula != null) {
      if (scope === "row") {
        s.adapter = makeRowFormulaAdapter(formula, null, xSource, shape);
      } else if (scope === "entry") {
        s.adapter = makeEntryFormulaAdapter(formula, null, shape);
      } else if (scope === "sheet") {
        s.groupAdapter = makeSheetFormulaAdapter(formula, null, shape);
      }
    }
  } else {
    if (adapter) s.adapter = adapter;
    if (groupAdapter) s.groupAdapter = groupAdapter;
  }
  series.push(s);

  // Series children are renderers (foreground z)
  for (const child of spec.children ?? []) {
    if (isRendererWireType(child.type)) {
      const r = rendererSpecFromNode(child);
      if (r) renderers.push(r);
    }
  }
}

/**
 * Resolve the Lisp expression for a `dataSource:"formula"` series.
 * Presets carry it as a field on the series spec (`spec.formula`);
 * wire/template rows carry it as a child `formula` row. Prefer the
 * field; otherwise the first `formula` child wins. Returns `null`
 * when no formula is present (series renders empty).
 */
function resolveSeriesFormula(spec: GraphSpecNode): Option<string> {
  if (typeof spec.formula === "string" && spec.formula.length > 0) {
    return spec.formula;
  }
  for (const child of spec.children ?? []) {
    if (
      child.type === "formula" &&
      typeof child.formula === "string" &&
      child.formula.length > 0
    ) {
      return child.formula;
    }
  }
  return null;
}

function rendererSpecFromNode(
  node: GraphSpecNode,
): Option<Graph.RendererSpec> {
  const kind = rendererSlotKindOf(node.type, node.attributes ?? null);
  if (kind == null) return null;
  const attrs = node.attributes ?? null;
  const id = Json.string(attrs, "id") ?? kind;

  const spec: Graph.RendererSpec = { id, type: kind };
  const key = Json.string(attrs, "key");
  if (key != null) spec.key = key;
  const color = Json.string(attrs, "color");
  if (color != null) spec.color = color;
  const radius = Json.number(attrs, "radius");
  if (radius != null) spec.radius = radius;
  const offset = Json.number(attrs, "offset");
  if (offset != null) spec.offset = offset;
  const offsetType = Json.string(attrs, "offsetType");
  if (offsetType === "pct" || offsetType === "abs") spec.offsetType = offsetType;
  const center = Json.number(attrs, "center");
  if (center != null) spec.center = center;
  const barColor = Json.string(attrs, "barColor");
  if (barColor != null) spec.barColor = barColor;
  const topColor = Json.string(attrs, "topColor");
  if (topColor != null) spec.topColor = topColor;
  const bottomColor = Json.string(attrs, "bottomColor");
  if (bottomColor != null) spec.bottomColor = bottomColor;
  return spec;
}

/**
 * Decode an axis range (`yAxis` / `yRange`): `min` and `max` are required
 * numbers; a missing `center` is the midpoint.
 */
function decodeAxisRange(
  obj: Option<JsonObject>,
): Option<{ min: number; max: number; center: number }> {
  const min = Json.number(obj, "min");
  const max = Json.number(obj, "max");
  if (min == null || max == null) return null;
  return { min, max, center: Json.number(obj, "center") ?? (min + max) / 2 };
}

/** Decode a `DateUnit.Parts.Opt` padding (`xPadding`); `null` unless an object. */
function decodeDateParts(obj: Option<JsonObject>): Option<DateUnit.Parts.Opt> {
  if (obj == null) return null;
  const yrs = Json.number(obj, "yrs");
  const mths = Json.number(obj, "mths");
  const days = Json.number(obj, "days");
  const wks = Json.number(obj, "wks");
  return {
    ...(yrs != null ? { yrs } : {}),
    ...(mths != null ? { mths } : {}),
    ...(days != null ? { days } : {}),
    ...(wks != null ? { wks } : {}),
  };
}
